import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { PatchDto, SubEntityServiceClient } from '@sharedrest/identity.restservice';
import { HeaderService } from '@sharedcommon/services/header.service';
import { NeqAbstractFormViewComponent } from '@sharedcommon/components/validation/abstract-form-view.component';
import { TechnicalServiceClient } from '@sharedrest/data.restservice';
import { ToastService } from '@sharedcommon/services/toast.service';
import { UserService } from '@sharedcommon/services/user.service';
import { ValidationService } from '@sharedcommon/services/validation.service';
import { Constants } from '@sharedcommon/util/constants.util';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  templateUrl: './export-settings.page.html'
})
export class NeqExportSettingsPage extends NeqAbstractFormViewComponent implements OnInit {

  private readonly META_EXPORT_FORMATS = 'RAWMAT_EXPORT_FORMATS';

  get formatForm(): FormControl { return this.form.get('format') as FormControl; }

  form : FormGroup = new FormGroup({
    enabled: new FormControl(),
    format: new FormControl(),
    limitExport: new FormControl(null, [Validators.required, Validators.min(10), Validators.max(500), Validators.pattern(Constants.REGEX_INTEGER_POSITIVE)]),
    limitImport: new FormControl(null, [Validators.required, Validators.min(10), Validators.max(500), Validators.pattern(Constants.REGEX_INTEGER_POSITIVE)])
  });
  formats: string[];
  chosenFormats: [string, boolean][] = [];
  availableValues: string[] = [];

  constructor(
    private readonly headerService: HeaderService,
    private readonly technicalService: TechnicalServiceClient,
    private readonly entityService: SubEntityServiceClient,
    private readonly userService: UserService,
    private readonly toastService: ToastService,
    private readonly translationService: TranslateService,
    protected validationService: ValidationService
  ) {
    super(validationService);
    this.validationService.init();
    this.checkInForm(this.form);
  }

  ngOnInit() {
    this.headerService.setHeaderValues({
      basicLabel: this.translationService.instant('label.user.export.settings'),
      showGoBack: true,
      redirectionRoute: '/app'
    });

    const entity = this.userService.getEntityDto();
    this.form.get('enabled').setValue(entity.rawmatExportEnabled);
    this.form.get('format').setValue(entity.rawmatExportFormat);
    this.form.get('limitExport').setValue(entity.limitExport);
    this.form.get('limitImport').setValue(entity.limitImport);
    this.technicalService.getMeta(this.META_EXPORT_FORMATS).subscribe(meta => {
      if (meta?.value) {
        this.formats = meta.value.split(',');
        let chosenFormat: string[] ;
        if(entity.rawmatExportList != null){
          chosenFormat = entity.rawmatExportList.split(';');
        } else{
          chosenFormat = [""];
        }
        for(const format of this.formats) {
          this.form.addControl(format, new FormControl());

          if (chosenFormat.includes(format)){
            this.chosenFormats.push([format, true]);
            this.availableValues.push(format);
            this.form.get(format).setValue(true);
          } else {
            this.chosenFormats.push([format, false]);
            this.form.get(format).setValue(false);
          }
        }
      }
    });
  }

  onFormSubmit() {
    if (this.form.invalid) {
      return;
    }

    if (this.form.get('enabled').value && this.chosenFormats.filter(x => x[1]).length == 0) {
      this.toastService.toast(this.translationService.instant('label.user.export.settings.empty-default-format'));
      return;
    }

    const formData = this.form.value;
    const entity = this.userService.getEntityDto();
    const patch: PatchDto<any>[] = [
      { op: 'REPLACE', path: '/rawmatExportEnabled', value: formData['enabled'] },
      { op: 'REPLACE', path: '/rawmatExportList', value: this.availableValues.join(';') },
      { op: 'REPLACE', path: '/limitExport', value: formData['limitExport'],  },
      { op: 'REPLACE', path: '/limitImport', value: formData['limitImport'] }
    ];
    if (formData['enabled']) {
      patch.push({ op: 'REPLACE', path: '/rawmatExportFormat', value: formData['format'] })
    }
    this.entityService.patch(entity.id, patch).subscribe(() => {
      // update the entity of current user (so he can see the export changes)
      // other users will see the changes when they will change of entity (login or via the entity choice page)
      entity.rawmatExportEnabled = formData['enabled'];
      entity.rawmatExportFormat = formData['format'];
      entity.rawmatExportList = this.availableValues.join(';');
      entity.limitExport = formData['limitExport'];
      entity.limitImport = formData['limitImport'];
      this.userService.setEntityDto(entity);
      this.form.markAsPristine();

      this.toastService.toast(this.translationService.instant('label.user.export.settings.updated'));
    });
  }

  getFormGroup(): FormGroup {
    return this.form;
  }

  hasFormModifications(): boolean {
    return this.form.dirty;
  }

  onEnabled(event: MatCheckboxChange) {
    if (event.checked) {
      this.formatForm.setValidators(Validators.required);
      return;
    }
    this.formatForm.clearValidators();
  }

  showOptions(index, chosenFormat, event){
    this.chosenFormats[index][1] = event.checked;
    if (event.checked){
      this.availableValues.push(chosenFormat[0]);
    } else {
      this.availableValues.forEach((formatName, indexFormat) => {
        if (formatName == chosenFormat[0]){
          this.availableValues.splice(indexFormat, 1);
        }
      });
    }
    if (!(this.availableValues.includes(this.form.get('format').value)) && (this.availableValues.length > 0)){
      this.form.get('format').setValue(this.availableValues[0]);
    }
   }
}
